import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { RouteAnalysis } from '../services/api';
import { tollFeeApi } from '../services/api/pricing';
import { TollBoothLocation } from '../types/pricing';

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createIcon = (color: 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'gray') => {
  const fillColor = color === 'blue' ? '#007bff' : 
       color === 'green' ? '#28a745' : 
      color === 'red' ? '#dc3545' :
   color === 'orange' ? '#fd7e14' :
   color === 'gray' ? '#6c757d' :
   '#6f42c1';
  const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32"><path fill="${fillColor}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white"/></svg>`;
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const createTollBoothIcon = () => {
  const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
    <!-- Base/Road -->
    <rect x="0" y="26" width="32" height="6" fill="#555" opacity="0.6"/>
    <!-- Toll booth structure -->
    <rect x="8" y="12" width="16" height="14" fill="#6c757d" stroke="#444" stroke-width="1"/>
  <!-- Roof -->
    <path d="M 6 12 L 16 6 L 26 12 Z" fill="#495057" stroke="#333" stroke-width="1"/>
    <!-- Window -->
    <rect x="12" y="16" width="8" height="6" fill="#87ceeb" opacity="0.7"/>
    <!-- Barrier arm (raised) -->
    <rect x="24" y="22" width="2" height="4" fill="#dc3545"/>
    <rect x="22" y="22" width="8" height="2" fill="#dc3545" transform="rotate(-45 26 23)"/>
    <!-- Base pole -->
    <rect x="7" y="22" width="2" height="4" fill="#ffc107"/>
  </svg>`;
  
  return L.divIcon({
 html: svgIcon,
    className: 'custom-tollbooth-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const createTollPassedIcon = () => {
  const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
    <!-- Base/Road -->
    <rect x="0" y="26" width="32" height="6" fill="#555" opacity="0.6"/>
    <!-- Toll booth structure (orange theme) -->
    <rect x="8" y="12" width="16" height="14" fill="#fd7e14" stroke="#dc6509" stroke-width="1"/>
    <!-- Roof -->
  <path d="M 6 12 L 16 6 L 26 12 Z" fill="#dc6509" stroke="#c55a08" stroke-width="1"/>
    <!-- Window -->
    <rect x="12" y="16" width="8" height="6" fill="#ffe5cc" opacity="0.9"/>
    <!-- Checkmark overlay (passed) -->
    <circle cx="16" cy="19" r="5" fill="#28a745" opacity="0.9"/>
    <path d="M 14 19 L 15.5 20.5 L 18 17.5" stroke="white" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    <!-- Barrier arm (lowered/passed) -->
    <rect x="24" y="24" width="2" height="2" fill="#333"/>
    <rect x="24" y="24" width="6" height="2" fill="#333"/>
  </svg>`;
  
  return L.divIcon({
 html: svgIcon,
    className: 'custom-toll-passed-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const createTollPotentialIcon = () => {
  const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
<!-- Base/Road -->
 <rect x="0" y="26" width="32" height="6" fill="#555" opacity="0.6"/>
    <!-- Toll booth structure (purple theme) -->
    <rect x="8" y="12" width="16" height="14" fill="#6f42c1" stroke="#5a32a3" stroke-width="1"/>
    <!-- Roof -->
    <path d="M 6 12 L 16 6 L 26 12 Z" fill="#5a32a3" stroke="#4a2889" stroke-width="1"/>
    <!-- Window -->
    <rect x="12" y="16" width="8" height="6" fill="#e0d4f7" opacity="0.9"/>
    <!-- Question mark overlay (potential) -->
    <circle cx="16" cy="19" r="5" fill="#ffc107" opacity="0.9"/>
    <text x="16" y="23" font-size="8" font-weight="bold" fill="white" text-anchor="middle">?</text>
    <!-- Barrier arm (raised/potential) -->
    <rect x="24" y="22" width="2" height="4" fill="#ffc107"/>
    <rect x="22" y="22" width="8" height="2" fill="#ffc107" transform="rotate(-45 26 23)"/>
  </svg>`;
  
  return L.divIcon({
    html: svgIcon,
    className: 'custom-toll-potential-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
 popupAnchor: [0, -32],
  });
};

const homeIcon = createIcon('blue');
const destinationIcon = createIcon('red');
const tollPassedIcon = createTollPassedIcon(); // Toll fees that were passed
const tollPotentialIcon = createTollPotentialIcon(); // Toll fees that might apply
const tollBoothIcon = createTollBoothIcon(); // All other toll booths

interface RouteMapProps {
  analysis: RouteAnalysis | null;
  showActualRoute: boolean;
  showOptimalRoute: boolean;
  showTollFees?: boolean;
  showAllTollBooths?: boolean; // New prop to show all toll booths
}

const RouteMap: React.FC<RouteMapProps> = ({ 
  analysis, 
  showActualRoute, 
  showOptimalRoute,
  showTollFees = true,
  showAllTollBooths = true 
}) => {
  const [allTollBooths, setAllTollBooths] = useState<TollBoothLocation[]>([]);

  useEffect(() => {
    // Fetch all toll booth locations on component mount
    const fetchTollBooths = async () => {
      try {
        const response = await tollFeeApi.getTollBoothLocations();
        setAllTollBooths(response.data);
      } catch (error) {
        console.error('Error fetching toll booth locations:', error);
      }
    };

    if (showAllTollBooths) {
      fetchTollBooths();
    }
  }, [showAllTollBooths]);

  if (!analysis || !analysis.actualRoute || !analysis.optimalRoute) {
    return (
      <div className="alert alert-info">
      <i className="bi bi-info-circle me-2"></i>
        Select a shipping record to view route analysis
      </div>
    );
  }

  const center: [number, number] = [analysis.homeLat, analysis.homeLon];
  const destinationPos: [number, number] = [analysis.destinationLat, analysis.destinationLon];

  // Format currency for Vietnam
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
    currency: 'VND' 
    }).format(value);
  };

  // Get IDs of toll booths that are part of the route analysis
  const routeTollBoothIds = new Set(
    (analysis.tollFeeLocations || [])
    .filter(t => t.latitude && t.longitude)
      .map(t => `${t.latitude}-${t.longitude}`)
  );

  // Use all GPS points from the actual route - don't filter
  const actualRoutePoints = analysis.actualRoute.routePoints || [];

  return (
    <MapContainer
      center={center}
  zoom={10}
      style={{ height: '500px', width: '100%' }}
  scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; OpenStreetMap contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* Home marker */}
  <Marker position={[analysis.homeLat, analysis.homeLon]} icon={homeIcon}>
        <Popup>
        <strong>Home</strong><br />
     Xã Giao Long, Vĩnh Long
        </Popup>
      </Marker>

      {/* Destination marker with circle indicator */}
    <Marker position={destinationPos} icon={destinationIcon}>
        <Popup>
   <strong>Destination</strong><br />
  {analysis.destination}<br />
       {analysis.provinceName && `${analysis.provinceName}`}<br />
      <small className="text-muted">
   ({analysis.destinationLat.toFixed(6)}, {analysis.destinationLon.toFixed(6)})
    </small>
        </Popup>
    </Marker>
      
      {/* Circle around destination (1km radius) to show destination area */}
 <Circle
     center={destinationPos}
   radius={1000}
        pathOptions={{
     color: '#dc3545',
       fillColor: '#dc3545',
   fillOpacity: 0.1,
          weight: 2,
          dashArray: '5, 5'
        }}
      />

   {/* Toll Fee Location Markers from Route Analysis (Passed/Potential) */}
      {showTollFees && analysis.tollFeeLocations && analysis.tollFeeLocations.map((toll, index) => {
     // Only show toll fees with coordinates
  if (!toll.latitude || !toll.longitude) return null;
     
        const icon = toll.isPassed ? tollPassedIcon : tollPotentialIcon;
        const position: [number, number] = [toll.latitude, toll.longitude];
      
        return (
  <Marker key={`toll-${toll.tollFeeId}-${index}`} position={position} icon={icon}>
      <Popup>
    <div>
   <strong style={{ color: toll.isPassed ? '#fd7e14' : '#6f42c1' }}>
      {toll.isPassed ? '✓ Passed' : 'Potential'} Toll Fee
           </strong><br />
      <strong>{toll.locationName}</strong><br />
     <small className="text-muted">{toll.locationType}</small><br />
 <strong>Fee: {formatCurrency(toll.fee)}</strong><br />
    {toll.address && (
        <>
         <small>{toll.address}</small><br />
          </>
         )}
  {toll.notes && (
         <small className="text-muted">{toll.notes}</small>
 )}
  </div>
       </Popup>
   </Marker>
      );
      })}

  {/* All Toll Booth Locations (gray markers) */}
      {showAllTollBooths && allTollBooths.map((booth, index) => {
     const boothKey = `${booth.latitude}-${booth.longitude}`;
        
    // Skip if this toll booth is already shown as part of route analysis
        if (routeTollBoothIds.has(boothKey)) return null;
     
        const position: [number, number] = [booth.latitude, booth.longitude];
     
        return (
  <Marker key={`booth-${index}`} position={position} icon={tollBoothIcon}>
      <Popup>
       <div>
     <strong style={{ color: '#6c757d' }}>
          🚧 Toll Booth
     </strong><br />
        <strong>{booth.name || 'Toll Booth'}</strong><br />
      <small className="text-muted">
   {booth.districtName ? `${booth.districtName}, ` : ''}{booth.provinceName}
      </small><br />
            {booth.address && (
   <>
         <small>{booth.address}</small><br />
     </>
        )}
        {booth.notes && (
  <>
             <small className="text-muted">{booth.notes}</small><br />
    </>
    )}
       <small className="text-info">
 {booth.tollFeeCount} fee tier{booth.tollFeeCount > 1 ? 's' : ''} available
      </small>
    </div>
       </Popup>
     </Marker>
        );
      })}

      {/* Actual route (GPS data) - Red - Draw ALL GPS points */}
      {showActualRoute && actualRoutePoints.length > 0 && (
        <Polyline
          positions={actualRoutePoints.map(p => [p.latitude, p.longitude])}
   color="#dc3545"
          weight={4}
    opacity={0.7}
 />
      )}

      {/* Optimal route (OSRM) - Green */}
      {showOptimalRoute && analysis.optimalRoute.routePoints.length > 0 && (
      <Polyline
 positions={analysis.optimalRoute.routePoints.map(p => [p.latitude, p.longitude])}
          color="#28a745"
      weight={4}
 opacity={0.7}
          dashArray="10, 10"
      />
      )}
    </MapContainer>
  );
};

export default RouteMap;
